import {
  type McpClientId,
  resolveMcpClientForAuthType,
} from "@arcadeai/ui-kit/mcp-gateway-connect";
import { Flag, Plug, Sparkles } from "lucide-react";
import { useState } from "react";
import type { OnboardingGateway } from "@/app/onboarding/api/get-onboarding-progress";
import { useGetOnboardingProgress } from "@/app/onboarding/api/hooks";
import { OnboardingJtbdRecapCard } from "@/app/onboarding-first-win/components/onboarding-jtbd-recap-card";
import type { OnboardingSidebarStepDef } from "@/app/onboarding-first-win/components/onboarding-sidebar";
import { useLeaveOnboarding } from "@/app/onboarding-first-win/hooks/use-leave-onboarding";
import {
  deriveSidebarStepStatus,
  type OnboardingStep,
} from "@/app/onboarding-first-win/lib/onboarding-shared";

export function useOnboardingOverlayState() {
  const leaveOnboarding = useLeaveOnboarding();
  const { data: onboardingProgress, isPending: isOnboardingProgressPending } =
    useGetOnboardingProgress({
      poll: true,
    });
  const cachedStep = onboardingProgress?.step ?? "jtbd";
  // Gateway create optimistically advances to connect, but a refetch can briefly
  // return jtbd before the engine lists the new gateway — mirror deriveCurrentStep.
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  const step = (
    onboardingProgress?.gateway != null && cachedStep === "jtbd"
      ? "connect"
      : cachedStep
  ) as OnboardingStep;
  const isStepLoading =
    isOnboardingProgressPending && onboardingProgress === undefined;
  const [selectedClient, setSelectedClient] = useState<McpClientId | undefined>(
    undefined
  );

  const gateway: OnboardingGateway | null = onboardingProgress?.gateway ?? null;
  const clientForNav =
    gateway && selectedClient
      ? resolveMcpClientForAuthType(selectedClient, gateway.auth_type)
      : undefined;
  const jtbd = gateway?.jtbd ?? null;
  const services = gateway?.services ?? [];

  const handleSkip = () => {
    leaveOnboarding({
      to: "/orgs/$organizationId/projects/$projectId/playground/chat",
      properties: {
        current_step: step,
        ...(jtbd?.id != null ? { jtbd_id: jtbd.id } : {}),
        ...(gateway != null && jtbd == null ? { gateway_custom: true } : {}),
      },
    });
  };

  const { step1Status, step2Status, step3Status } = isStepLoading
    ? ({
        step1Status: "future",
        step2Status: "future",
        step3Status: "future",
      } as const)
    : deriveSidebarStepStatus({ currentStep: step });

  const step1Recap = gateway ? (
    <OnboardingJtbdRecapCard
      description={jtbd?.description ?? null}
      name={jtbd?.name ?? gateway?.name ?? null}
      services={services}
    />
  ) : undefined;

  const sidebarSteps: OnboardingSidebarStepDef[] = [
    {
      id: "jtbd",
      label: "Pick your goal",
      icon: <Flag className="size-4" />,
      status: step1Status,
      recap: step1Recap,
    },
    {
      id: "connect",
      label: "Connect & get to work",
      icon: <Plug className="size-4" />,
      status: step2Status,
    },
    {
      id: "done",
      label: "You're done",
      icon: <Sparkles className="size-4" />,
      status: step3Status,
    },
  ];

  return {
    handleSkip,
    isStepLoading,
    step,
    sidebarSteps,
    gateway,
    clientForNav,
    onSelectClient: setSelectedClient,
  };
}
